import { useState } from "react";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";

const WRITE = "write";
const PREVIEW = "preview";

// Both panes carry this so the box doesn't jump when the tab changes.
const DEFAULT_PANE_CLASSNAME = "min-h-[18rem] max-h-[36rem] overflow-y-auto";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** Caps the source and shows a live character count, so the user can see why
   *  the field stops taking keystrokes. */
  maxLength?: number;
  invalid?: boolean;
  /** Focus the source on mount. Inside a dialog this keeps the first keystrokes
   *  out of the tab bar, which is otherwise the first thing focus lands on. */
  autoFocus?: boolean;
  /** Sizing for both panes. Overrides the default min/max height. */
  paneClassName?: string;
  className?: string;
  "aria-label"?: string;
}

/**
 * Markdown source editor with GitHub-style Write / Preview tabs. Controlled —
 * the caller owns the value, validation, and any save affordance.
 */
export function MarkdownEditor({
  value,
  onChange,
  onBlur,
  id,
  placeholder,
  disabled = false,
  readOnly = false,
  maxLength,
  invalid = false,
  autoFocus = false,
  paneClassName,
  className,
  "aria-label": ariaLabel,
}: MarkdownEditorProps) {
  // Read-only surfaces open on the rendered view; the source is a click away.
  const [tab, setTab] = useState(readOnly ? PREVIEW : WRITE);

  const panes = paneClassName ?? DEFAULT_PANE_CLASSNAME;

  return (
    <Tabs
      value={tab}
      onValueChange={setTab}
      className={cn(
        "gap-0 overflow-hidden rounded-md border border-input bg-transparent shadow-xs",
        disabled && "opacity-50",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-input bg-muted/40 p-1.5">
        <TabsList className="h-auto rounded-none bg-transparent p-0">
          <TabsTrigger value={WRITE} className="h-7">
            Write
          </TabsTrigger>
          <TabsTrigger value={PREVIEW} className="h-7">
            Preview
          </TabsTrigger>
        </TabsList>
        {tab === WRITE && (
          <div className="flex items-center gap-2 pr-1.5 text-xs font-semibold text-muted-foreground">
            <span>Markdown is supported</span>
            {maxLength !== undefined && (
              <>
                <span aria-hidden>·</span>
                <span
                  className={cn("tabular-nums", value.length >= maxLength && "text-destructive")}
                >
                  {value.length.toLocaleString()} / {maxLength.toLocaleString()}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <TabsContent value={WRITE} className="p-2">
        <Textarea
          id={id}
          aria-label={ariaLabel}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          autoFocus={autoFocus}
          aria-invalid={invalid}
          // 14px/24px matches the preview's `prose-sm`. Textarea's `md:text-sm`
          // is emitted later and takes its line-height from `--tw-leading`, so
          // the override has to be a `leading-*` utility.
          // Fainter than a plain textarea's: a multi-line markdown placeholder
          // otherwise reads as text the user already wrote.
          className={cn(
            panes,
            "font-mono text-sm leading-6 [tab-size:2] placeholder:text-muted-foreground/70 disabled:opacity-100"
          )}
        />
      </TabsContent>

      {/* Matches the textarea's own padding and border box so the frame keeps
          its height across a tab change. */}
      <TabsContent value={PREVIEW} className="p-2">
        <div className={cn(panes, "rounded-md border border-transparent px-3 py-2")}>
          {value.trim() ? (
            <MarkdownPreview>{value}</MarkdownPreview>
          ) : (
            <p className="text-sm italic text-muted-foreground">Nothing to preview</p>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}
